import type { SVGProps } from "react";

export type IconName =
  | "dictation"
  | "guideline"
  | "secretary"
  | "doctor"
  | "copy"
  | "comment"
  | "mail"
  | "workflow"
  | "result"
  | "adoption"
  | "document"
  | "followup"
  | "lab"
  | "medication"
  | "registry"
  | "shield"
  | "europe"
  | "trust"
  | "menu";

function baseProps(props: SVGProps<SVGSVGElement>) {
  return {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
    ...props,
  };
}

function DictationIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps(props)}>
      <path d="M12 4a3 3 0 0 1 3 3v4a3 3 0 1 1-6 0V7a3 3 0 0 1 3-3Z" />
      <path d="M6.5 11a5.5 5.5 0 0 0 11 0" />
      <path d="M12 16.5V20" />
      <path d="M8.5 20h7" />
    </svg>
  );
}

function GuidelineIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps(props)}>
      <path d="M8 4.5h8l3 3v11a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2v-12a2 2 0 0 1 2-2Z" />
      <path d="M16 4.5V8h3" />
      <path d="m9 12 1.8 1.8L15 9.5" />
      <path d="M9 17h6" />
    </svg>
  );
}

function SecretaryIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps(props)}>
      <rect x="4" y="5" width="16" height="14" rx="2.5" />
      <path d="M8 9h8" />
      <path d="M8 13h5" />
      <path d="M8 17h4" />
      <path d="m16 16 1.2 1.2L20 14.4" />
    </svg>
  );
}

function DoctorIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps(props)}>
      <path d="M9.1 7.2a2.9 2.9 0 1 1 5.8 0v1.1a2.9 2.9 0 1 1-5.8 0Z" />
      <path d="M7.2 20v-4.2a3.5 3.5 0 0 1 3.5-3.5h2.6a3.5 3.5 0 0 1 3.5 3.5V20" />
      <path d="M9.4 20v-4.1l2.6-1.8 2.6 1.8V20" />
      <path d="M8.6 12.8 7 15.2" />
      <path d="m15.4 12.8 1.6 2.4" />
      <circle cx="9.1" cy="16.8" r="0.9" />
      <circle cx="14.9" cy="16.8" r="0.9" />
    </svg>
  );
}

function CopyIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps(props)}>
      <rect x="4.5" y="9.5" width="7" height="7" rx="1.8" />
      <rect x="12.5" y="4.5" width="7" height="7" rx="1.8" />
      <rect x="12.5" y="12.5" width="7" height="7" rx="1.8" />
      <path d="M11.5 13h1" />
      <path d="M12 11.5v1" />
    </svg>
  );
}

function CommentIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps(props)}>
      <path d="M6.5 6.5h11a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H11l-3.5 2.8V16.5h-1a2 2 0 0 1-2-2v-6a2 2 0 0 1 2-2Z" />
      <path d="M9 10.5h6" />
      <path d="M9 13.5h4.5" />
    </svg>
  );
}

function MailIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps(props)}>
      <rect x="4" y="6" width="16" height="12" rx="2.5" />
      <path d="m5.5 8 6.5 5 6.5-5" />
    </svg>
  );
}

function WorkflowIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps(props)}>
      <rect x="3.5" y="4.5" width="17" height="12.5" rx="2.2" />
      <path d="M9 20h6" />
      <path d="M12 17v3" />
      <rect x="6.4" y="7" width="5.1" height="6.2" rx="1.1" />
      <path d="M7.7 8.7h2.5" />
      <path d="M7.7 10.6h2.5" />
      <path d="M7.7 12.5h1.7" />
      <path d="M16 8.1a2.7 2.7 0 1 1 0 5.4" />
      <path d="M16 8.1v2.7h2.7" />
    </svg>
  );
}

function ResultIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps(props)}>
      <path d="M5.2 7.4h4.2" />
      <path d="m6 9.6 1.3-2.7 1.3 5 1.2-2.4h2.1" />
      <circle cx="6.4" cy="15.8" r="0.55" fill="currentColor" stroke="none" />
      <circle cx="8.6" cy="15.8" r="0.55" fill="currentColor" stroke="none" />
      <circle cx="10.8" cy="15.8" r="0.55" fill="currentColor" stroke="none" />
      <path d="M13.8 5.5h4.5a2.2 2.2 0 0 1 2.2 2.2v8.6a2.2 2.2 0 0 1-2.2 2.2h-8.4" />
      <path d="M13.8 18.5H10" />
      <path d="M20.5 7.9h-3.4" />
    </svg>
  );
}

function AdoptionIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps(props)}>
      <path d="M4.5 19.5V6" />
      <path d="M4.5 19.5h15" />
      <path d="M7.8 16.8v-4.2" />
      <path d="M12 16.8V9.6" />
      <path d="M16.2 16.8v-5.5" />
      <path d="m7.4 10.2 3.2-3.1 2.7 2.4 4.2-4.2" />
      <path d="M17.5 5.3h2v2" />
    </svg>
  );
}

function DocumentIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps(props)}>
      <path d="M8 4.5h8l3 3v11a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2v-12a2 2 0 0 1 2-2Z" />
      <path d="M16 4.5V8h3" />
      <path d="M9 12h6" />
      <path d="M9 15h6" />
      <path d="M9 18h4" />
    </svg>
  );
}

function FollowupIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps(props)}>
      <rect x="5" y="5" width="14" height="14" rx="3" />
      <path d="M8 3.5v3" />
      <path d="M16 3.5v3" />
      <path d="M8 11h8" />
      <path d="M8 15h4" />
      <path d="m14.5 16 1.5 1.5 3-3" />
    </svg>
  );
}

function LabIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps(props)}>
      <path d="M8 3.5v5l-3 6.2A2.3 2.3 0 0 0 7 18h10a2.3 2.3 0 0 0 2-3.3L16 8.5v-5" />
      <path d="M9 3.5h6" />
      <path d="M7.5 14h9" />
    </svg>
  );
}

function MedicationIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps(props)}>
      <path d="m8 7 2.5-2.5a2.8 2.8 0 0 1 4 4L12 11" />
      <path d="m8 7 4 4" />
      <path d="m5.5 12.5 6 6" />
      <path d="M4.5 19.5 11 13" />
      <path d="M14 14.5h5.5" />
    </svg>
  );
}

function RegistryIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps(props)}>
      <rect x="5" y="4.5" width="14" height="15" rx="2.5" />
      <path d="M9 9h6" />
      <path d="M9 13h6" />
      <path d="M9 17h4" />
      <path d="m15.5 17 1.2 1.2L19 15.9" />
    </svg>
  );
}

function ShieldIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps(props)}>
      <path d="M8.1 17.8A3.9 3.9 0 0 1 4.2 14c0-1.9 1.4-3.5 3.2-3.8.4-2.5 2.4-4.2 4.9-4.2 2.8 0 4.9 2 5.1 4.8 1.5.4 2.6 1.8 2.6 3.4a3.8 3.8 0 0 1-3.8 3.8H14.7l-1 1.7h-2.9l-1-1.7Z" />
      <circle cx="10" cy="13.8" r="0.65" fill="currentColor" stroke="none" />
      <circle cx="12.5" cy="13.8" r="0.65" fill="currentColor" stroke="none" />
      <circle cx="15" cy="13.8" r="0.65" fill="currentColor" stroke="none" />
    </svg>
  );
}

function EuropeIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps(props)}>
      <path d="M4.4 18.8V6.6" />
      <path d="M4.4 18.8H19.6" />
      <path d="M12.2 5.9a4 4 0 1 1 0 8" />
      <path d="M12.2 5.9v4h4" />
    </svg>
  );
}

function TrustIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps(props)}>
      <path d="M8 4.8h8l2.2 2.3v11a2.2 2.2 0 0 1-2.2 2.2H8a2.2 2.2 0 0 1-2.2-2.2V7A2.2 2.2 0 0 1 8 4.8Z" />
      <path d="M16 4.8V7h2.2" />
      <path d="m8.7 11.1 1 1 1.8-1.9" />
      <path d="M12.8 11.4h3" />
      <path d="m8.7 15.4 1 1 1.8-1.9" />
      <path d="M12.8 15.7h3" />
    </svg>
  );
}

function MenuIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps(props)}>
      <path d="M4 7h16" />
      <path d="M4 12h16" />
      <path d="M4 17h16" />
    </svg>
  );
}

export function MarketingIcon({
  name,
  className,
}: {
  name: IconName;
  className?: string;
}) {
  switch (name) {
    case "dictation":
      return <DictationIcon className={className} />;
    case "guideline":
      return <GuidelineIcon className={className} />;
    case "secretary":
      return <SecretaryIcon className={className} />;
    case "doctor":
      return <DoctorIcon className={className} />;
    case "copy":
      return <CopyIcon className={className} />;
    case "comment":
      return <CommentIcon className={className} />;
    case "mail":
      return <MailIcon className={className} />;
    case "workflow":
      return <WorkflowIcon className={className} />;
    case "result":
      return <ResultIcon className={className} />;
    case "adoption":
      return <AdoptionIcon className={className} />;
    case "document":
      return <DocumentIcon className={className} />;
    case "followup":
      return <FollowupIcon className={className} />;
    case "lab":
      return <LabIcon className={className} />;
    case "medication":
      return <MedicationIcon className={className} />;
    case "registry":
      return <RegistryIcon className={className} />;
    case "shield":
      return <ShieldIcon className={className} />;
    case "europe":
      return <EuropeIcon className={className} />;
    case "trust":
      return <TrustIcon className={className} />;
    case "menu":
      return <MenuIcon className={className} />;
    default:
      return null;
  }
}
